/**
 * Leviathan Web - Settings Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 *
 * System Configuration & Administration
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Settings,
  Users,
  Building2,
  Shield,
  Mail,
  X,
  Check,
  Clock,
  UserPlus,
} from 'lucide-react';
import { API_URL } from '@/config/api';

// Types
interface TenantSettings {
  id: string;
  companyName: string | null;
  legalName: string | null;
  taxId: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
  phone: string | null;
  fax: string | null;
  email: string | null;
  website: string | null;
  defaultPaymentTerms: number | null;
  defaultTaxRate: number | null;
  fiscalYearStart: number | null;
  dateFormat: string | null;
  timeFormat: string | null;
  timezone: string | null;
  currency: string | null;
  orderPrefix: string | null;
  invoicePrefix: string | null;
  customerPrefix: string | null;
  defaultUnit: string | null;
  trackInventory: boolean;
  requirePO: boolean;
}

interface User {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

interface Invitation {
  id: string;
  email: string;
  role: string;
  expiresAt: string;
  createdAt: string;
  invitedBy: {
    firstName: string | null;
    lastName: string | null;
    email: string;
  };
}

interface Stats {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  pendingInvitations: number;
}

type TabType = 'company' | 'users' | 'preferences' | 'security';

/**
 * Form Field Component
 */
function FormField({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
}) {
  return (
    <div>
      <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm transition-all"
      />
    </div>
  );
}

/**
 * Stat Card Component
 */
function StatCard({
  label,
  value,
  status,
}: {
  label: string;
  value: number;
  status?: 'operational' | 'warning' | 'critical';
}) {
  const statusColor =
    status === 'operational'
      ? 'bg-success-green'
      : status === 'warning'
        ? 'bg-warning-amber'
        : status === 'critical'
          ? 'bg-error-red'
          : 'bg-steel';

  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-amber" />
      <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-amber" />
      <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-amber" />
      <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-amber" />

      <div className="bg-panel-dark border border-panel-border p-4 clip-path-octagon relative">
        {status && (
          <div className="absolute top-3 right-3">
            <div
              className={`w-2 h-2 ${statusColor} rounded-full animate-pulse`}
              style={{ boxShadow: '0 0 8px currentColor' }}
            />
          </div>
        )}
        <div className="text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
          {label}
        </div>
        <div
          className="text-2xl font-mono font-bold text-amber"
          style={{ textShadow: '0 0 10px rgba(255, 149, 0, 0.6)' }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

/**
 * Role Badge Component
 */
function getRoleColor(role: string): string {
  switch (role) {
    case 'OWNER':
      return 'text-amber bg-amber/10';
    case 'SUPER_ADMIN':
      return 'text-amber bg-amber/10';
    case 'ADMIN':
      return 'text-steel bg-steel/10';
    case 'MANAGER':
      return 'text-success-green bg-success-green/10';
    case 'USER':
      return 'text-text-secondary bg-text-secondary/10';
    case 'DRIVER':
      return 'text-warning-amber bg-warning-amber/10';
    case 'READONLY':
      return 'text-text-tertiary bg-text-tertiary/10';
    default:
      return 'text-text-secondary bg-text-secondary/10';
  }
}

/**
 * Settings Page Component
 */
const SettingsPage = () => {
  const { accessToken } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('company');
  const [settings, setSettings] = useState<TenantSettings | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('USER');

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    fetchSettings();
    fetchStats();
  }, []);

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
      fetchInvitations();
    }
  }, [activeTab]);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/tenant`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data.settings);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/stats`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/users`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchInvitations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/invitations`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setInvitations(data.invitations);
      }
    } catch (error) {
      console.error('Failed to fetch invitations:', error);
    }
  };

  const saveSettings = async () => {
    if (!settings) return;
    setIsSaving(true);
    setMessage(null);

    try {
      const response = await fetch(`${API_URL}/api/settings/tenant`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(settings),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Settings saved successfully' });
        setTimeout(() => setMessage(null), 3000);
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.error || 'Failed to save settings' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setIsSaving(false);
    }
  };

  const sendInvitation = async () => {
    setMessage(null);

    try {
      const response = await fetch(`${API_URL}/api/settings/invitations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ email: inviteEmail, role: inviteRole }),
      });

      if (response.ok) {
        const data = await response.json();
        setMessage({ type: 'success', text: `Invitation sent to ${inviteEmail}` });
        setShowInviteModal(false);
        setInviteEmail('');
        setInviteRole('USER');
        fetchInvitations();
        fetchStats();

        // Show invite URL for development
        if (data.invitation?.inviteUrl) {
          console.log('Invite URL:', data.invitation.inviteUrl);
        }
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.error || 'Failed to send invitation' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to send invitation' });
    }
  };

  const cancelInvitation = async (id: string) => {
    try {
      const response = await fetch(`${API_URL}/api/settings/invitations/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.ok) {
        fetchInvitations();
        fetchStats();
        setMessage({ type: 'success', text: 'Invitation cancelled' });
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (error) {
      console.error('Failed to cancel invitation:', error);
    }
  };

  const changePassword = async () => {
    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }

    if (newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters' });
      return;
    }

    setMessage(null);

    try {
      const response = await fetch(`${API_URL}/api/settings/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Password changed successfully' });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setTimeout(() => setMessage(null), 3000);
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.error || 'Failed to change password' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to change password' });
    }
  };

  const updateField = (field: keyof TenantSettings, value: any) => {
    if (settings) {
      setSettings({ ...settings, [field]: value });
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    { id: 'company', label: 'Company', icon: <Building2 className="w-4 h-4" /> },
    { id: 'users', label: 'Users', icon: <Users className="w-4 h-4" /> },
    { id: 'preferences', label: 'Preferences', icon: <Settings className="w-4 h-4" /> },
    { id: 'security', label: 'Security', icon: <Shield className="w-4 h-4" /> },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6">
          <h1
            className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
            style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
          >
            Settings
          </h1>
          <p className="text-text-secondary text-sm font-mono mt-1">
            System Configuration & Administration
          </p>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Users" value={stats.totalUsers} />
          <StatCard label="Active" value={stats.activeUsers} status="operational" />
          <StatCard label="Inactive" value={stats.inactiveUsers} />
          <StatCard
            label="Pending Invites"
            value={stats.pendingInvitations}
            status={stats.pendingInvitations > 0 ? 'warning' : undefined}
          />
        </div>
      )}

      {/* Message */}
      {message && (
        <div
          className={`p-4 border flex items-center justify-between ${
            message.type === 'success'
              ? 'bg-success-green/10 border-success-green/50 text-success-green'
              : 'bg-error-red/10 border-error-red/50 text-error-red'
          } font-mono text-sm`}
        >
          <div className="flex items-center space-x-2">
            {message.type === 'success' ? (
              <Check className="w-4 h-4" />
            ) : (
              <X className="w-4 h-4" />
            )}
            <span>{message.text}</span>
          </div>
          <button onClick={() => setMessage(null)}>
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex space-x-1 border-b border-panel-border">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center space-x-2 px-6 py-3 font-mono text-sm uppercase tracking-wider transition-all ${
              activeTab === tab.id
                ? 'text-amber border-b-2 border-amber bg-amber/5'
                : 'text-text-secondary hover:text-text-primary hover:bg-panel-border/20'
            }`}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="relative stat-card">
        <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
        <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
        <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
        <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

        <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="text-amber font-mono animate-pulse">Loading settings...</div>
            </div>
          ) : (
            <>
              {/* Company Tab */}
              {activeTab === 'company' && settings && (
                <div className="space-y-6">
                  <h2 className="text-xl font-display text-text-primary uppercase tracking-wide mb-6">
                    Company Information
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      label="Company Name"
                      value={settings.companyName || ''}
                      onChange={(v) => updateField('companyName', v)}
                    />
                    <FormField
                      label="Legal Name"
                      value={settings.legalName || ''}
                      onChange={(v) => updateField('legalName', v)}
                    />
                    <FormField
                      label="Tax ID / EIN"
                      value={settings.taxId || ''}
                      onChange={(v) => updateField('taxId', v)}
                    />
                    <FormField
                      label="Website"
                      value={settings.website || ''}
                      onChange={(v) => updateField('website', v)}
                    />
                    <FormField
                      label="Phone"
                      value={settings.phone || ''}
                      onChange={(v) => updateField('phone', v)}
                    />
                    <FormField
                      label="Email"
                      value={settings.email || ''}
                      onChange={(v) => updateField('email', v)}
                      type="email"
                    />
                  </div>

                  <h3 className="text-lg font-display text-text-primary uppercase tracking-wide mt-8 mb-4">
                    Address
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <FormField
                        label="Street Address"
                        value={settings.address || ''}
                        onChange={(v) => updateField('address', v)}
                      />
                    </div>
                    <FormField
                      label="City"
                      value={settings.city || ''}
                      onChange={(v) => updateField('city', v)}
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        label="State"
                        value={settings.state || ''}
                        onChange={(v) => updateField('state', v)}
                      />
                      <FormField
                        label="ZIP"
                        value={settings.zip || ''}
                        onChange={(v) => updateField('zip', v)}
                      />
                    </div>
                  </div>

                  <div className="flex justify-end mt-8">
                    <button
                      onClick={saveSettings}
                      disabled={isSaving}
                      className="bg-amber hover:bg-amber/90 text-background font-display font-bold py-3 px-8 transition-all uppercase tracking-wider disabled:opacity-50"
                      style={{ boxShadow: '0 0 20px rgba(255, 149, 0, 0.4)' }}
                    >
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              )}

              {/* Users Tab */}
              {activeTab === 'users' && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h2 className="text-xl font-display text-text-primary uppercase tracking-wide">
                      User Management
                    </h2>
                    <button
                      onClick={() => setShowInviteModal(true)}
                      className="flex items-center space-x-2 bg-amber hover:bg-amber/90 text-background font-mono text-sm py-2 px-4 transition-all uppercase tracking-wider"
                    >
                      <UserPlus className="w-4 h-4" />
                      <span>Invite User</span>
                    </button>
                  </div>

                  {/* Pending Invitations */}
                  {invitations.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-sm font-mono text-text-secondary uppercase tracking-wider mb-3 flex items-center space-x-2">
                        <Mail className="w-4 h-4" />
                        <span>Pending Invitations</span>
                      </h3>
                      <div className="space-y-2">
                        {invitations.map((inv) => (
                          <div
                            key={inv.id}
                            className="flex items-center justify-between bg-background p-3 border border-panel-border"
                          >
                            <div className="flex items-center space-x-3">
                              <Clock className="w-4 h-4 text-warning-amber" />
                              <span className="text-text-primary font-mono">{inv.email}</span>
                              <span
                                className={`text-xs px-2 py-1 rounded font-mono ${getRoleColor(inv.role)}`}
                              >
                                {inv.role}
                              </span>
                            </div>
                            <div className="flex items-center space-x-4">
                              <span className="text-text-tertiary font-mono text-xs">
                                Expires {formatDate(inv.expiresAt)}
                              </span>
                              <button
                                onClick={() => cancelInvitation(inv.id)}
                                className="text-error-red hover:text-error-red/80 font-mono text-xs uppercase"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Users Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="border-b border-panel-border">
                        <tr>
                          <th className="text-left px-4 py-3 text-text-secondary text-xs uppercase tracking-widest font-mono">
                            User
                          </th>
                          <th className="text-left px-4 py-3 text-text-secondary text-xs uppercase tracking-widest font-mono">
                            Role
                          </th>
                          <th className="text-left px-4 py-3 text-text-secondary text-xs uppercase tracking-widest font-mono">
                            Status
                          </th>
                          <th className="text-left px-4 py-3 text-text-secondary text-xs uppercase tracking-widest font-mono">
                            Last Login
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((user) => (
                          <tr
                            key={user.id}
                            className="border-b border-panel-border hover:bg-amber/5 transition-colors"
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 bg-steel/20 rounded-full flex items-center justify-center text-steel font-mono text-sm">
                                  {user.firstName?.[0] || ''}
                                  {user.lastName?.[0] || ''}
                                </div>
                                <div>
                                  <div className="text-text-primary font-mono">
                                    {user.firstName} {user.lastName}
                                  </div>
                                  <div className="text-text-tertiary font-mono text-xs">
                                    {user.email}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`text-xs px-2 py-1 rounded font-mono ${getRoleColor(user.role)}`}
                              >
                                {user.role}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`text-xs px-2 py-1 rounded font-mono ${
                                  user.isActive
                                    ? 'text-success-green bg-success-green/10'
                                    : 'text-error-red bg-error-red/10'
                                }`}
                              >
                                {user.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-text-tertiary font-mono text-sm">
                              {user.lastLoginAt ? formatDate(user.lastLoginAt) : 'Never'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Preferences Tab */}
              {activeTab === 'preferences' && settings && (
                <div className="space-y-6">
                  <h2 className="text-xl font-display text-text-primary uppercase tracking-wide mb-6">
                    Business Preferences
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      label="Default Payment Terms (Days)"
                      value={settings.defaultPaymentTerms?.toString() || '30'}
                      onChange={(v) => updateField('defaultPaymentTerms', parseInt(v) || 30)}
                      type="number"
                    />
                    <FormField
                      label="Default Tax Rate (%)"
                      value={settings.defaultTaxRate?.toString() || '0'}
                      onChange={(v) => updateField('defaultTaxRate', parseFloat(v) || 0)}
                      type="number"
                    />
                    <FormField
                      label="Timezone"
                      value={settings.timezone || 'America/Chicago'}
                      onChange={(v) => updateField('timezone', v)}
                    />
                    <FormField
                      label="Currency"
                      value={settings.currency || 'USD'}
                      onChange={(v) => updateField('currency', v)}
                    />
                  </div>

                  <h3 className="text-lg font-display text-text-primary uppercase tracking-wide mt-8 mb-4">
                    Numbering Prefixes
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <FormField
                      label="Order Prefix"
                      value={settings.orderPrefix || 'ORD'}
                      onChange={(v) => updateField('orderPrefix', v)}
                    />
                    <FormField
                      label="Invoice Prefix"
                      value={settings.invoicePrefix || 'INV'}
                      onChange={(v) => updateField('invoicePrefix', v)}
                    />
                    <FormField
                      label="Customer Prefix"
                      value={settings.customerPrefix || 'CUST'}
                      onChange={(v) => updateField('customerPrefix', v)}
                    />
                  </div>

                  <div className="flex justify-end mt-8">
                    <button
                      onClick={saveSettings}
                      disabled={isSaving}
                      className="bg-amber hover:bg-amber/90 text-background font-display font-bold py-3 px-8 transition-all uppercase tracking-wider disabled:opacity-50"
                      style={{ boxShadow: '0 0 20px rgba(255, 149, 0, 0.4)' }}
                    >
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              )}

              {/* Security Tab */}
              {activeTab === 'security' && (
                <div className="space-y-6">
                  <h2 className="text-xl font-display text-text-primary uppercase tracking-wide mb-6">
                    Change Password
                  </h2>

                  <div className="max-w-md space-y-4">
                    <FormField
                      label="Current Password"
                      value={currentPassword}
                      onChange={setCurrentPassword}
                      type="password"
                    />
                    <FormField
                      label="New Password"
                      value={newPassword}
                      onChange={setNewPassword}
                      type="password"
                    />
                    <FormField
                      label="Confirm New Password"
                      value={confirmPassword}
                      onChange={setConfirmPassword}
                      type="password"
                    />

                    <button
                      onClick={changePassword}
                      className="bg-amber hover:bg-amber/90 text-background font-display font-bold py-3 px-8 transition-all uppercase tracking-wider"
                      style={{ boxShadow: '0 0 20px rgba(255, 149, 0, 0.4)' }}
                    >
                      Update Password
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-background/80 flex items-center justify-center z-50">
          <div className="bg-panel-dark border border-amber p-6 w-full max-w-md relative">
            <div className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2 border-amber" />
            <div className="absolute top-0 right-0 w-4 h-4 border-r-2 border-t-2 border-amber" />
            <div className="absolute bottom-0 left-0 w-4 h-4 border-l-2 border-b-2 border-amber" />
            <div className="absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2 border-amber" />

            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-display text-text-primary uppercase tracking-wide">
                Invite User
              </h2>
              <button onClick={() => setShowInviteModal(false)}>
                <X className="w-5 h-5 text-text-secondary hover:text-text-primary" />
              </button>
            </div>

            <div className="space-y-4">
              <FormField
                label="Email Address"
                value={inviteEmail}
                onChange={setInviteEmail}
                type="email"
                placeholder="user@example.com"
              />

              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  Role
                </label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
                >
                  <option value="USER">User</option>
                  <option value="MANAGER">Manager</option>
                  <option value="ADMIN">Admin</option>
                  <option value="DRIVER">Driver</option>
                  <option value="READONLY">Read Only</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end space-x-4 mt-6">
              <button
                onClick={() => setShowInviteModal(false)}
                className="bg-panel-border hover:bg-steel/20 text-text-primary font-mono py-2 px-6 transition-all uppercase text-sm"
              >
                Cancel
              </button>
              <button
                onClick={sendInvitation}
                disabled={!inviteEmail}
                className="bg-amber hover:bg-amber/90 text-background font-mono py-2 px-6 transition-all uppercase text-sm disabled:opacity-50"
              >
                Send Invite
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
